import React from 'react';
import { Phone, ArrowRight, Star, TrendingUp, Search, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';
export function Hero() {
  const phoneNumber = '(949) 555-0117';
  const phoneLink = 'tel:9495550117';
  return <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-slate-50" data-id="element-70">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-71">
        <div className="absolute -top-1/2 -right-1/4 w-full h-full bg-gradient-to-b from-brand-100/50 to-transparent rounded-full blur-3xl opacity-60 transform rotate-12" data-id="element-72" />
        <div className="absolute -bottom-1/2 -left-1/4 w-full h-full bg-gradient-to-t from-brand-50 to-transparent rounded-full blur-3xl opacity-60" data-id="element-73" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-74">
        <div className="lg:grid lg:grid-cols-12 lg:gap-16 items-center" data-id="element-75">
          {/* Left Column: Copy & CTAs */}
          <div className="lg:col-span-6 text-center lg:text-left mb-16 lg:mb-0" data-id="element-76">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-77">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-100 text-brand-700 text-sm font-semibold mb-6" data-id="element-78">
                <span className="flex h-2 w-2 rounded-full bg-brand-600" data-id="element-79"></span>
                Orange County's Premier Performance SEO Agency
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-6" data-id="element-80">
                Rankings that move{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-brand-400" data-id="element-81">
                  revenue
                </span>
                , not vanity metrics.
              </h1>

              <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-82">
                We are the SEO partner Orange County marketing teams call when
                they need traffic that actually converts — without the agency
                fluff.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-10" data-id="element-83">
                <a href={phoneLink} className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-bold rounded-xl text-white bg-brand-600 hover:bg-brand-700 shadow-lg shadow-brand-500/30 hover:shadow-brand-500/50 transition-all gap-2" data-id="element-84">
                  <Phone className="w-5 h-5" data-id="element-85" />
                  Call {phoneNumber}
                </a>
                <a href="#audit" className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 border-2 border-slate-200 text-base font-bold rounded-xl text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-300 transition-all gap-2" data-id="element-86">
                  Get my free audit
                  <ArrowRight className="w-5 h-5" data-id="element-87" />
                </a>
              </div>

              {/* Trust Strip */}
              <div className="pt-6 border-t border-slate-200/60" data-id="element-88">
                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-6 text-sm font-medium text-slate-500" data-id="element-89">
                  <div className="flex items-center gap-1.5" data-id="element-90">
                    <div className="flex text-accent-500" data-id="element-91">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-92" />)}
                    </div>
                    <span data-id="element-93">4.9★ Clutch rating</span>
                  </div>
                  <div className="hidden sm:block w-1 h-1 rounded-full bg-slate-300" data-id="element-94"></div>
                  <span data-id="element-95">Trusted by 200+ marketing teams</span>
                  <div className="hidden sm:block w-1 h-1 rounded-full bg-slate-300" data-id="element-96"></div>
                  <span data-id="element-97">Featured in SEJ</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Column: Animated Dashboard Mock */}
          <div className="lg:col-span-6 relative" data-id="element-98">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="relative rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-slate-200/50 overflow-hidden" data-id="element-99">
              {/* Dashboard Header */}
              <div className="bg-slate-50 border-b border-slate-100 px-6 py-4 flex items-center justify-between" data-id="element-100">
                <div className="flex items-center gap-2" data-id="element-101">
                  <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-102"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-400" data-id="element-103"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-104"></div>
                </div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-105">
                  Organic Revenue Growth
                </div>
              </div>

              {/* Dashboard Content */}
              <div className="p-6" data-id="element-106">
                {/* Top Stats */}
                <div className="grid grid-cols-3 gap-4 mb-8" data-id="element-107">
                  {[{
                  label: 'Organic Traffic',
                  value: '+142%',
                  icon: TrendingUp
                }, {
                  label: 'Non-Brand Clicks',
                  value: '+89%',
                  icon: Search
                }, {
                  label: 'Conversions',
                  value: '+215%',
                  icon: BarChart3
                }].map((stat, i) => <div key={i} className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-108">
                      <stat.icon className="w-5 h-5 text-brand-500 mb-2" data-id="element-109" />
                      <div className="text-xl font-bold text-slate-900" data-id="element-110">
                        {stat.value}
                      </div>
                      <div className="text-xs text-slate-500 font-medium" data-id="element-111">
                        {stat.label}
                      </div>
                    </div>)}
                </div>

                {/* Chart Area */}
                <div className="relative h-48 w-full border-b border-l border-slate-100 mb-6" data-id="element-112">
                  {/* Grid lines */}
                  <div className="absolute inset-0 flex flex-col justify-between" data-id="element-113">
                    {[...Array(4)].map((_, i) => <div key={i} className="w-full border-t border-slate-50 border-dashed" data-id="element-114"></div>)}
                  </div>

                  {/* Animated Line Chart */}
                  <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-115">
                    <motion.path d="M0,90 C20,85 30,60 50,50 C70,40 80,20 100,10" fill="none" stroke="url(#gradient)" strokeWidth="4" strokeLinecap="round" initial={{
                    pathLength: 0,
                    opacity: 0
                  }} animate={{
                    pathLength: 1,
                    opacity: 1
                  }} transition={{
                    duration: 2,
                    ease: 'easeInOut',
                    delay: 0.5
                  }} data-id="element-116" />
                    <defs data-id="element-117">
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-118">
                        <stop offset="0%" stopColor="#38bdf8" data-id="element-119" />
                        <stop offset="100%" stopColor="#0284c7" data-id="element-120" />
                      </linearGradient>
                    </defs>
                  </svg>

                  {/* Animated Data Points */}
                  <motion.div className="absolute w-3 h-3 bg-brand-600 rounded-full border-2 border-white shadow-sm" style={{
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)'
                }} initial={{
                  scale: 0,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  delay: 1.5
                }} data-id="element-121" />
                  <motion.div className="absolute w-4 h-4 bg-brand-600 rounded-full border-2 border-white shadow-md" style={{
                  left: '100%',
                  top: '10%',
                  transform: 'translate(-50%, -50%)'
                }} initial={{
                  scale: 0,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  delay: 2.2
                }} data-id="element-122" />
                </div>

                {/* Keyword Pills */}
                <div className="space-y-3" data-id="element-123">
                  <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2" data-id="element-124">
                    Target Keywords
                  </div>
                  {[{
                  kw: 'b2b software solutions',
                  from: 34,
                  to: 2,
                  delay: 1
                }, {
                  kw: 'enterprise cloud migration',
                  from: 28,
                  to: 1,
                  delay: 1.3
                }, {
                  kw: 'managed IT services irvine',
                  from: 15,
                  to: 3,
                  delay: 1.6
                }].map((item, i) => <div key={i} className="flex items-center justify-between bg-white border border-slate-100 rounded-lg p-3 shadow-sm" data-id="element-125">
                      <span className="text-sm font-medium text-slate-700" data-id="element-126">
                        {item.kw}
                      </span>
                      <div className="flex items-center gap-3" data-id="element-127">
                        <span className="text-xs text-slate-400" data-id="element-128">
                          Pos {item.from}
                        </span>
                        <ArrowRight className="w-3 h-3 text-slate-300" data-id="element-129" />
                        <motion.span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded" initial={{
                      opacity: 0,
                      x: -10
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: item.delay,
                      duration: 0.5
                    }} data-id="element-130">
                          Pos {item.to}
                        </motion.span>
                      </div>
                    </div>)}
                </div>
              </div>
            </motion.div>

            {/* Decorative floating element */}
            <motion.div className="absolute -right-6 -bottom-6 bg-white p-4 rounded-xl shadow-xl border border-slate-100 hidden lg:block" initial={{
            y: 20,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            delay: 2.5,
            type: 'spring'
          }} data-id="element-131">
              <div className="flex items-center gap-3" data-id="element-132">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center" data-id="element-133">
                  <TrendingUp className="w-5 h-5 text-green-600" data-id="element-134" />
                </div>
                <div data-id="element-135">
                  <div className="text-sm font-bold text-slate-900" data-id="element-136">
                    Goal Reached
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-137">
                    Q3 Revenue Target
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}